import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Eye, Reply, Calendar } from "lucide-react";
import { toast } from "sonner";

const statusInfo = (s) => ({
  pendente: { label: "Pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  em_analise: { label: "Em análise", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  respondida: { label: "Respondida", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  agendada: { label: "Agendada", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  concluida: { label: "Concluída", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelada: { label: "Cancelada", cls: "bg-rose-100 text-rose-800 border-rose-200" },
})[s] || { label: s, cls: "" };

export default function AdminConsultorias() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const load = () => {
    const params = {};
    if (filter && filter !== "all") params.status = filter;
    if (typeFilter && typeFilter !== "all") params.type = typeFilter;
    api.get("/admin/consultancies", { params }).then(r => setItems(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, typeFilter]);

  const openReply = (c) => { setOpen(c); setReply(c.response || ""); setScheduled(c.scheduled_date ? c.scheduled_date.slice(0, 10) : ""); setNewStatus(c.status); };

  const send = async () => {
    if (!open) return;
    try {
      const payload = { status: newStatus };
      if (reply) payload.response = reply;
      if (scheduled) payload.scheduled_date = new Date(scheduled).toISOString();
      await api.put(`/admin/consultancies/${open.id}`, payload);
      toast.success("Atualizado");
      setOpen(null); load();
    } catch { toast.error("Erro"); }
  };

  return (
    <AdminLayout title="Consultorias">
      <Card className="rounded-xl"><CardContent className="p-4 flex flex-wrap gap-3 items-end">
        <div><p className="text-xs text-muted-foreground mb-1">Status</p>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="em_analise">Em análise</SelectItem>
              <SelectItem value="respondida">Respondida</SelectItem>
              <SelectItem value="agendada">Agendada</SelectItem>
              <SelectItem value="concluida">Concluída</SelectItem>
              <SelectItem value="cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><p className="text-xs text-muted-foreground mb-1">Tipo</p>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="presencial">Presencial</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <Card className="rounded-xl mt-4"><CardContent className="p-0 overflow-x-auto">
        <Table><TableHeader><TableRow>
          <TableHead>Aluno</TableHead><TableHead>Tipo</TableHead><TableHead>Tema</TableHead><TableHead>Solicitado em</TableHead><TableHead>Data desejada</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ação</TableHead>
        </TableRow></TableHeader>
          <TableBody>
            {items.map(c => {
              const s = statusInfo(c.status);
              return (
                <TableRow key={c.id}>
                  <TableCell><p className="font-medium">{c.user_name}</p><p className="text-xs text-muted-foreground font-mono-id">{c.user_ceap_id}</p></TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{c.type === "online" ? "Online" : "Presencial"}</Badge></TableCell>
                  <TableCell><p className="text-sm font-medium">{c.theme}</p><p className="text-xs text-muted-foreground line-clamp-1">{c.description}</p></TableCell>
                  <TableCell className="text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : "—"}</TableCell>
                  <TableCell className="text-xs">{c.requested_date ? new Date(c.requested_date).toLocaleDateString("pt-BR") : "—"}</TableCell>
                  <TableCell><Badge className={s.cls} variant="outline">{s.label}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openReply(c)} data-testid={`consultancy-respond-${c.id}`}><Reply className="w-4 h-4 mr-1" /> Atender</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">Nenhuma consultoria</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-[560px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Atender consultoria</DialogTitle>
            <DialogDescription>Defina status, data agendada (se presencial) e envie a resposta ao aluno.</DialogDescription>
          </DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-3 text-sm">
                <p><strong>Aluno:</strong> {open.user_name}</p>
                <p><strong>Tipo:</strong> {open.type === "online" ? "Online" : "Presencial"}</p>
                <p><strong>Tema:</strong> {open.theme}</p>
                <p className="mt-2 whitespace-pre-wrap">{open.description}</p>
              </div>
              <div><Label>Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="em_analise">Em análise</SelectItem>
                    <SelectItem value="respondida">Respondida</SelectItem>
                    <SelectItem value="agendada">Agendada</SelectItem>
                    <SelectItem value="concluida">Concluída</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {open.type === "presencial" && (
                <div><Label>Data agendada</Label><Input type="date" value={scheduled} onChange={(e) => setScheduled(e.target.value)} /></div>
              )}
              <div><Label>Resposta para o aluno</Label><Textarea rows={6} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Resposta técnica para o aluno…" data-testid="consultancy-reply" /></div>
              <Button onClick={send} className="w-full" data-testid="consultancy-send"><Reply className="w-4 h-4 mr-1" /> Enviar atualização</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
